import { IOaiUser, IUser } from 'features/auth/types'
import { getLongAgentName, getUserListQuery } from 'features/claims/utils'
import { useState } from 'react'
import { FaSearch, FaTimesCircle, FaUserEdit } from 'react-icons/fa'
import { DropdownWithUserSearch } from 'components/DropdownWithUserSearch/DropdownWithUserSearch'
import { OUTBOUND_SUPPORT } from 'utils/constants'
import { useDebounces } from 'utils/hooks'
import { httpTenantsService } from 'utils/http'
import { notificator } from 'utils/notificator/notificator'
import storage, { StorageEnum } from 'utils/storage'
import { ASSIGN_UNASSIGN, INITIAL_PAGINATION, USER_LIMIT } from '../../ClaimDetails/Internal/Constants'

export interface SelectedOaiUser {
  oaiUserName: string | null
  oaiUserId: string | null
}

interface IProps {
  oaiUser: (value: SelectedOaiUser) => void
}

export const BulkAssignAndUnAssign = ({ oaiUser }: IProps) => {
  const currentUser: IUser = storage.get(StorageEnum.currentUser)
  const [fullUserList, setFullUserList] = useState<IOaiUser[]>([]) // Original list
  const [userList, setUserList] = useState<IOaiUser[]>([]) // Filtered list
  const [userListPagination, setUserListPagination] = useState(INITIAL_PAGINATION)
  const [hasMoreUsers, setHasMoreUsers] = useState(true)

  const getUserList = (text?: string, offset?: number) => {
    const { isSearch, updatedUrl } = getUserListQuery(offset === 0 ? 0 : userListPagination.offset, text)
    setDataUserList(updatedUrl, isSearch)
  }

  const openEmail = () => {
    window.open(`mailto:${OUTBOUND_SUPPORT}?subject=&body=`)
  }

  const setDataUserList = (url: string, isSearch: boolean) => {
    httpTenantsService
      .get(url)
      .then((res) => {
        const users = res.data
        if (users) {
          const updateUserList = isSearch ? [] : [...fullUserList]
          const filteredUsers = [...updateUserList, ...users.data]
          const removeDuplicates = filteredUsers.filter(
            (obj, index, self) => index === self.findIndex((t: IOaiUser) => t.userGuid === obj.userGuid),
          )
          setFullUserList(removeDuplicates) // Update original list
          setUserList(removeDuplicates) // Update filtered list
          if (users.data.length === 0 || users.data.length < USER_LIMIT) {
            setHasMoreUsers(false)
            return
          } else {
            setHasMoreUsers(true)
          }
          setUserListPagination({ count: users.count, limit: users.limit, offset: users.offset + USER_LIMIT })
        }
      })
      .catch(() => {
        notificator.notify({
          type: 'error',
          heading: 'Something went wrong',
          message: (
            <>
              Please refresh the page and try again. If you’re still <br /> having issues, contact{' '}
              <span className="text-decoration-underline" onClick={openEmail}>
                {' '}
                Customer Support
              </span>
              .
            </>
          ),
          toastOptions: {
            icon: <FaTimesCircle />,
            closeButton: true,
          },
        })
        setHasMoreUsers(false)
      })
  }

  const userSearchByName = useDebounces((text: string) => {
    if (text?.length <= 0) {
      setUserList(fullUserList)
    } else {
      const searchText = text.toLowerCase()
      const filteredUsers = fullUserList.filter((user) => {
        const availableName = getAvailableUsername(user).toLowerCase()
        const username = user.username.toLowerCase()
        return availableName.includes(searchText) || username.includes(searchText)
      })
      setUserList(filteredUsers)
    }
  }, 800)

  const dropdownInitLoad = () => {
    setHasMoreUsers(true)
    getUserList()
  }

  const dropdownClosed = () => {
    setUserListPagination({ ...userListPagination, count: 0, offset: 0, limit: 0 })
    setUserList([])
  }

  const handleSelectedUser = (user: IOaiUser | ASSIGN_UNASSIGN) => {
    const { userName, userId } = getUserDetail(user, currentUser)
    oaiUser({ oaiUserId: userId!, oaiUserName: userName })
  }

  return (
    <DropdownWithUserSearch
      height="32px"
      label="Assign"
      labelCount={{ Min: 1, Total: 19 }}
      items={userList.map((user: IOaiUser) => ({
        userName: getAvailableUsername(user),
        onClick: () => handleSelectedUser(user),
      }))}
      searchText={(t: any) => {
        userSearchByName(t)
      }}
      initialLoad={dropdownInitLoad}
      close={dropdownClosed}
      emptyState={getEmptyState(hasMoreUsers)}
      icon={<FaUserEdit />}
      dataLength={userListPagination.offset}
      next={(t: string) => getUserList(t)}
      hasMore={hasMoreUsers}
      assignUnAssign={(value: ASSIGN_UNASSIGN) => handleSelectedUser(value)}
    />
  )
}

export function getAvailableUsername(user: IOaiUser) {
  let name
  if (user.givenName && user.familyName) {
    name = `${user.givenName} ${user.familyName}`
  } else if (user.givenName) {
    name = user.givenName
  } else if (user.familyName) {
    name = user.familyName
  } else if (user.username) {
    name = user.username
  } else {
    name = user.email
  }
  return name
}

export function getEmptyState(isEmpty: boolean) {
  return isEmpty ? (
    <div className="text-center position-absolute top-50 w-100">Loading...</div>
  ) : (
    <div className="no-user-found">
      <FaSearch />
      <br />
      No users were found <br /> matching your search
    </div>
  )
}

export const getUserDetail = (user: IOaiUser | ASSIGN_UNASSIGN, currentUser: IUser) => {
  let userName
  let userId

  if (user === 'Assign') {
    userName = getLongAgentName(currentUser)
    userId = currentUser?.id
  } else if (user === 'Unassign') {
    userName = null
    userId = null
  } else {
    userName = getAvailableUsername(user)
    userId = user.userGuid
  }

  return {
    userName,
    userId,
  }
}
